import { ChevronIcon } from '@/common/icons/ChevronIcon';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import i18n from '@/i18n';
import { cn } from '@/lib/utils';
import type { DefaultTFuncReturn } from 'i18next';
import { Check } from 'lucide-react';
import * as React from 'react';
import { Controller } from 'react-hook-form';

type MaybeI18n = React.ReactNode | DefaultTFuncReturn;

interface Option {
  value: string | number;
  label: MaybeI18n;
}

interface IProps extends Omit<React.ComponentProps<typeof Button>, 'onChange'> {
  name: string;
  control: any;
  options?: Option[];
  label?: MaybeI18n;
  placeholder?: MaybeI18n;
  description?: MaybeI18n;
  required?: boolean;
  disabled?: boolean;
  notRequired?: boolean;
  withoutDescription?: boolean;
  asNumber?: boolean;
  className?: string;
  buttonClass?: string;
}

function normalizeLabel(value?: MaybeI18n): string {
  if (value === undefined || value === null) return '';
  if (React.isValidElement(value))
    return (value.props?.children ?? '').toString();
  if (Array.isArray(value)) return value.join('');
  return String(value);
}

export const ComboboxControl: React.FC<IProps> = ({
  name,
  control,
  options = [],
  label,
  placeholder,
  description,
  required,
  disabled,
  notRequired,
  withoutDescription,
  asNumber,
  className,
  buttonClass,
  ...props
}) => {
  const [open, setOpen] = React.useState(false);

  const sortedOptions = React.useMemo(() => {
    return [...options].sort((a, b) =>
      normalizeLabel(a.label).localeCompare(normalizeLabel(b.label)),
    );
  }, [options]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('relative', className)}>
          {label && (
            <FormLabel>
              {normalizeLabel(label)}{' '}
              {required && <span className="text-red">*</span>}
            </FormLabel>
          )}

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  {...props}
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={disabled}
                  className={cn(
                    'w-full justify-between relative',
                    disabled && 'opacity-50 cursor-not-allowed',
                    buttonClass,
                  )}
                >
                  {field?.value
                    ? normalizeLabel(
                        options.find(
                          (opt) => String(opt?.value) === String(field?.value),
                        )?.label,
                      )
                    : normalizeLabel(placeholder)}
                  <span className="absolute right-3">
                    <ChevronIcon />
                  </span>
                </Button>
              </FormControl>
            </PopoverTrigger>

            <PopoverContent className="w-full p-0">
              <Command>
                <CommandInput
                  placeholder={String(i18n.t('search'))}
                  className="h-9"
                  disabled={disabled}
                />
                <CommandList>
                  {sortedOptions.length === 0 && (
                    <CommandEmpty>{String(i18n.t('no.data'))}</CommandEmpty>
                  )}
                  <CommandGroup>
                    {sortedOptions.map((opt) => {
                      return (
                        <CommandItem
                          key={String(opt.value)}
                          value={normalizeLabel(opt.label)}
                          onSelect={() => {
                            const finalValue = asNumber
                              ? Number(opt.value)
                              : opt.value;
                            field.onChange(finalValue);
                            setOpen(false);
                          }}
                        >
                          {normalizeLabel(opt.label)}
                          <Check
                            className={cn(
                              'ml-auto',
                              String(field.value) === String(opt.value)
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {!withoutDescription && (
            <div className="absolute -bottom-[18px] right-0">
              {description && (
                <FormDescription>{normalizeLabel(description)}</FormDescription>
              )}
              {!notRequired && <FormMessage />}
            </div>
          )}
        </FormItem>
      )}
    />
  );
};
